/**
 * V1.9.3 — Measurement system utilities.
 *
 * Blueprint AI's internal geometry is always METRIC (metres everywhere).
 * This module just converts to/from the user's preferred display system
 * and provides the right grid-snap step.
 *
 *   "imperial" (US): snap to 1-foot grid (0.3048 m), display X'-Y"
 *                    where applicable; cabinets/appliances align to the
 *                    standard 16" stud grid (0.4064 m).
 *   "metric"   (EU): snap to 25 cm grid (0.25 m), display in metres.
 *
 * Snap convention:
 *   gridStep_m(system)        — generic move/drop step (1ft vs 25cm)
 *   moduleStep_m(system)      — appliance/cabinet alignment (16" vs 60cm)
 *   snap(value_m, system)     — round to nearest gridStep
 *   fmtLength(m, system)      — render "12'-6\"" or "3.81 m"
 */

const FT_TO_M = 0.3048;
const IN_TO_M = 0.0254;

export const DEFAULT_SYSTEM = "imperial"; // US default — change per region.

export function gridStep_m(system) {
  return system === "metric" ? 0.25 : 1 * FT_TO_M; // 0.25m or 1ft
}

export function moduleStep_m(system) {
  return system === "metric" ? 0.60 : 16 * IN_TO_M; // 60cm or 16" stud spacing
}

export function snap(value_m, system) {
  const step = gridStep_m(system);
  return Math.round(value_m / step) * step;
}

export function snapModule(value_m, system) {
  const step = moduleStep_m(system);
  return Math.round(value_m / step) * step;
}

export function fmtLength(m, system) {
  if (!Number.isFinite(m)) return "—";
  if (system === "metric") {
    return m >= 1 ? `${m.toFixed(2)} m` : `${(m * 100).toFixed(0)} cm`;
  }
  // imperial — total inches → ft + in.
  const totalIn = m / IN_TO_M;
  const ft = Math.floor(totalIn / 12);
  const inch = Math.round(totalIn - ft * 12);
  if (ft === 0) return `${inch}"`;
  if (inch === 0) return `${ft}'`;
  return `${ft}'-${inch}"`;
}

export function fmtSize(width_m, depth_m, system) {
  return `${fmtLength(width_m, system)} × ${fmtLength(depth_m, system)}`;
}
